import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ClockIcon,
  PencilIcon,
  LockClosedIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useToast, ToastContainer } from '../common/Toast';
import Dialog from '../common/Dialog';
import AssignSiteModal from './AssignSiteModal';
import userService, { UserDetail as UserDetailType, Role, LoginAttempt, AuditLog } from '../../services/users';
import permissionService from '../../services/permissionService';
import siteService from '../../services/sites';
import { userAssignmentsService } from '../../services/userAssignments';

type TabType = 'profil' | 'connexions' | 'actions';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { toasts, showSuccess, showError } = useToast();
  
  const [user, setUser] = useState<UserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('profil');
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [showAssignSiteModal, setShowAssignSiteModal] = useState(false);
  const [availableSites, setAvailableSites] = useState<Array<{id: number, name: string, code: string}>>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [assigningSite, setAssigningSite] = useState(false);
  const [userAssignments, setUserAssignments] = useState<any[]>([]);
  const [loginHistory, setLoginHistory] = useState<LoginAttempt[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [globalRoles, setGlobalRoles] = useState<string[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);


  useEffect(() => {
    if (userId) {
      loadUserData();
    } else {
      console.log('⚠️ userId manquant dans les paramètres');
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    if (activeTab === 'connexions' || activeTab === 'actions') {
      loadHistoryData(activeTab);
    }
  }, [userId, activeTab]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les données utilisateur depuis le backend
      const userData = await userService.getUserById(userId!);
      const normalizedUser: UserDetailType = {
        ...userData,
        id: String(userData.id),
        name: userData.name || userData.full_name || userData.username,
        is_blocked: userData.is_blocked ?? !userData.is_active,
        created_at: userData.created_at || userData.date_joined || new Date().toISOString(),
        updated_at: userData.updated_at || userData.last_login || userData.created_at || new Date().toISOString(),
        locale: userData.locale || 'fr',
        twofa_enabled: userData.twofa_enabled ?? false,
        status: userData.status || (userData.is_active ? 'ACTIVE' : 'BLOCKED'),
        site_roles: userData.site_roles || [],
        site_sessions: userData.site_sessions || [],
        twofa: userData.twofa || { user_id: String(userData.id), is_enabled: false, recovery_codes: [] },
        audit_logs: userData.audit_logs || [],
        is_staff: userData.is_staff,
        is_superuser: userData.is_superuser,
        role: userData.role,
      };
      setUser(normalizedUser);
      
      // Récupérer les assignations de sites séparément
      console.log('🔍 Récupération des assignations pour l\'utilisateur:', userId);
      try {
        const assignments = await userAssignmentsService.getAssignments(userId!);
        console.log('✅ Assignations récupérées:', assignments);
        setUserAssignments(assignments);
      } catch (assignmentError) {
        console.warn('⚠️ Erreur lors de la récupération des assignations:', assignmentError);
        setUserAssignments([]);
      }

      // Rôles globaux (plateforme)
      try {
        const userRoles = await permissionService.getUserRoles(Number(userId));
        const roleNames = userRoles
          .filter((ur) => ur.is_active !== false)
          .map((ur) => {
            const entry = ur as { role_name?: string; role?: { name?: string; display_name?: string } };
            return entry.role_name
              || entry.role?.display_name
              || entry.role?.name
              || '';
          })
          .filter(Boolean);
        setGlobalRoles([...new Set(roleNames)]);
      } catch (roleError) {
        console.warn('⚠️ Erreur lors de la récupération des rôles globaux:', roleError);
        setGlobalRoles([]);
      }
      
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement de l\'utilisateur:', error);
      let errorMessage = 'Impossible de charger les données de l\'utilisateur';
      if (error.response?.status === 404) {
        errorMessage = 'Utilisateur non trouvé (404)';
      } else if (error.response?.status === 401) {
        errorMessage = 'Non autorisé - Veuillez vous reconnecter (401)';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erreur serveur (500)';
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      showError('Erreur', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadHistoryData = async (tab: TabType) => {
    if (!userId) return;

    try {
      setHistoryLoading(true);
      setHistoryError(null);

      if (tab === 'connexions') {
        const attempts = await userService.getUserLoginHistory(userId);
        setLoginHistory(attempts);
      } else if (tab === 'actions') {
        const logs = await userService.getUserAuditLogs(userId);
        setAuditLogs(logs);
      }
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement de l\'historique:', error);
      const message = error.response?.status === 404
        ? 'Endpoint historique non disponible sur le serveur'
        : 'Impossible de charger l\'historique';
      setHistoryError(message);
      if (tab === 'connexions') {
        setLoginHistory([]);
      } else {
        setAuditLogs([]);
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleBlockUser = async () => {
    if (!user || !blockReason.trim()) return;
    
    try {
      
      // Appel API pour bloquer l'utilisateur
      await userService.blockUser(user.id, blockReason);
      showSuccess('Succès', 'Utilisateur bloqué avec succès');
      setShowBlockDialog(false);
      setBlockReason('');
      
      // Recharger les données
      await loadUserData();
      if (activeTab === 'actions') {
        await loadHistoryData('actions');
      }
    } catch (error: any) {
      console.error('❌ Erreur lors du blocage:', error);
      showError('Erreur', `Impossible de bloquer l'utilisateur: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const handleUnblockUser = async () => {
    if (!user) return;
    
    try {
      
      await userService.unblockUser(user.id, 'Déblocage administrateur');
      showSuccess('Succès', 'Utilisateur débloqué avec succès');
      
      // Recharger les données
      await loadUserData();
      if (activeTab === 'actions') {
        await loadHistoryData('actions');
      }
    } catch (error: any) {
      console.error('❌ Erreur lors du déblocage:', error);
      showError('Erreur', `Impossible de débloquer l'utilisateur: ${error.message || 'Erreur inconnue'}`);
    }
  };

    // Fonction pour charger les sites et rôles disponibles
  const loadAvailableSitesAndRoles = async () => {
    try {
      setAssigningSite(true);
     
      // Charger les sites disponibles (exclure ceux déjà assignés)
      const sitesResponse = await siteService.getSites();
      console.log('🔍 Sites chargés:', sitesResponse);
      
      const assignedSiteIds = user?.site_roles?.map(sr => Number(sr.site.id)) || [];
      console.log('🔍 Sites déjà assignés:', assignedSiteIds);
      
      const availableSites = sitesResponse.filter(site => !assignedSiteIds.includes(site.id));
      console.log('🔍 Sites disponibles:', availableSites);
      
      setAvailableSites(availableSites);
      
      // Charger les rôles disponibles
      console.log('🔍 Chargement des rôles...');
      const rolesResponse = await userService.getRoles();
      console.log('🔍 Réponse des rôles:', rolesResponse);
      
             // Extraire les rôles de la réponse paginée
       let roles: Role[] = [];
       if (rolesResponse && typeof rolesResponse === 'object') {
         if (Array.isArray(rolesResponse)) {
           // Si c'est déjà un tableau (fallback)
           roles = rolesResponse;
         } else if ('results' in rolesResponse && Array.isArray(rolesResponse.results)) {
           // Si c'est une réponse paginée
           roles = rolesResponse.results;
         } else {
           console.warn('⚠️ Structure de réponse inattendue:', rolesResponse);
         }
       }
       
       // Filtrer le rôle SUPERADMIN
       roles = roles.filter(role => role.name !== 'SUPERADMIN');
      
      console.log('🔍 Rôles extraits:', roles);
      console.log('🔍 Nombre de rôles:', roles.length);
      
      if (roles.length > 0) {
        console.log('✅ Rôles valides, mise à jour du state avec', roles.length, 'rôles');
        setAvailableRoles(roles);
      } else {
        console.warn('⚠️ Aucun rôle trouvé');
        setAvailableRoles([]);
      }
      
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement des sites/rôles:', error);
      showError('Erreur', 'Impossible de charger les sites et rôles disponibles');
    } finally {
      setAssigningSite(false);
    }
  };

  // Fonction pour gérer l'assignation d'un site
  const handleAssignSite = async (siteId: number, roleId: string, sessionType: 'PERMANENTE' | 'TEMPORAIRE', isDefault: boolean, startDate?: string, endDate?: string) => {
    if (!userId) {
      alert('ID utilisateur non défini');
      return;
    }

    try {
      setLoading(true);
      
      // Utiliser le nouveau service unifié
      await userAssignmentsService.createAssignment(userId, {
        user: parseInt(userId),
        site: siteId,
        role: parseInt(roleId),
        session_type: sessionType,
        starts_at: startDate,
        ends_at: endDate,
        is_default: isDefault
      });

      // Recharger les données utilisateur et les assignations
      await loadUserData();
      
      // Fermer le modal
      setShowAssignSiteModal(false);
      
      // Afficher un message de succès
      alert('Site assigné avec succès !');
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'assignation du site:', error);
      alert(`Erreur lors de l'assignation: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };



  // Fonction pour ouvrir le modal d'assignation
  const openAssignSiteModal = async () => {
    try {
      // Charger d'abord les données avant d'ouvrir le modal
      await loadAvailableSitesAndRoles();
      // Ouvrir le modal seulement après avoir chargé les données
      setShowAssignSiteModal(true);
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'ouverture du modal:', error);
      showError('Erreur', 'Impossible de charger les données nécessaires pour l\'assignation');
    }
  };


  // Fonction pour formater les données des sites avec rôles
  const getFormattedSites = () => {
    console.log('🔍 Formatage des sites - Assignations disponibles:', userAssignments);
    
    // Utiliser les assignations récupérées séparément
    if (!userAssignments || !Array.isArray(userAssignments) || userAssignments.length === 0) {
      console.log('⚠️ Aucune assignation disponible');
      return [];
    }
    
    try {
      const formatted = userAssignments
        .filter((assignment) => assignment.is_active) // Filtrer seulement les assignations actives
        .map((assignment) => {
          const formattedSite = {
            id: assignment.site,
            name: assignment.site_name || 'Site non défini',
            code: assignment.site_code || 'N/A',
            role: assignment.role_label || 'Rôle non défini',
            assigned_at: assignment.created_at || new Date().toISOString(),
            is_default: assignment.is_default || false
          };
          
          return formattedSite;
        });
      
      console.log('✅ Sites formatés:', formatted);
      return formatted;
    } catch (error) {
      console.error('❌ Erreur lors du formatage des sites:', error);
      return [];
    }
  };

  // Fonction pour formater l'historique des connexions
  const getFormattedConnections = () => {
    return loginHistory.map((attempt) => ({
      id: String(attempt.id),
      date: attempt.created_at,
      ip: attempt.ip_address || 'N/A',
      user_agent: attempt.user_agent || 'N/A',
      status: attempt.success ? ('success' as const) : ('failure' as const),
      twoFactor: attempt.two_factor_required,
    }));
  };

  const formatAuditDetails = (log: AuditLog): string => {
    const payload = log.details ?? log.payload_json;
    if (!payload) {
      const parts: string[] = [];
      if (log.target_user_username) parts.push(`Cible: ${log.target_user_username}`);
      if (log.site_name) parts.push(`Site: ${log.site_name}`);
      if (log.role_label) parts.push(`Rôle: ${log.role_label}`);
      return parts.length ? parts.join(' · ') : 'Aucun détail';
    }
    if (typeof payload === 'string') return payload;

    const details = payload as Record<string, unknown>;
    const parts: string[] = [];
    if (details.reason) parts.push(`Motif: ${String(details.reason)}`);
    if (details.description) parts.push(String(details.description));
    if (details.permission) parts.push(`Permission: ${String(details.permission)}`);
    if (details.new_value !== undefined) parts.push(`Valeur: ${String(details.new_value)}`);
    if (log.site_name) parts.push(`Site: ${log.site_name}`);
    if (log.role_label) parts.push(`Rôle: ${log.role_label}`);
    if (log.target_user_username && !parts.some((p) => p.includes(log.target_user_username!))) {
      parts.push(`Cible: ${log.target_user_username}`);
    }

    if (parts.length) return parts.join(' · ');

    try {
      return JSON.stringify(details);
    } catch {
      return 'Détails indisponibles';
    }
  };

  const formatAuditAction = (log: AuditLog): string => {
    const actionLabels: Record<string, string> = {
      ASSIGN_SITE: 'Assignation de site',
      REMOVE_SITE: 'Suppression d\'assignation',
      UPDATE_ROLE: 'Modification de rôle',
      UPDATE_SESSION: 'Modification de session',
      BLOCK_USER: 'Blocage utilisateur',
      UNBLOCK_USER: 'Déblocage utilisateur',
      SEND_WELCOME_EMAIL: 'Email de bienvenue',
      UPDATE_SESSION_PERMISSION: 'Modification de permission',
      SET_DEFAULT_SITE: 'Site par défaut',
      ACTIVATE_ASSIGNMENT: 'Activation d\'assignation',
      DEACTIVATE_ASSIGNMENT: 'Désactivation d\'assignation',
      ACTIVATE_SESSION: 'Activation de session',
      DEACTIVATE_SESSION: 'Désactivation de session',
      BULK_CREATE_SESSIONS: 'Création de sessions',
      RESET_PASSWORD: 'Réinitialisation mot de passe',
      reset_password: 'Réinitialisation mot de passe',
      FORCE_PASSWORD_CHANGE: 'Changement de mot de passe forcé',
      force_password_change: 'Changement de mot de passe forcé',
    };
    return actionLabels[log.action] || log.action;
  };

  // Fonction pour formater l'historique des actions
  const getFormattedActions = () => {
    return auditLogs.map((log) => ({
      id: String(log.id),
      action: formatAuditAction(log),
      details: formatAuditDetails(log),
      actor: log.user_username || log.actor?.name || 'Système',
      date: log.created_at,
      ip: log.ip_address || 'N/A',
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données utilisateur...</p>
          <p className="text-sm text-gray-500 mt-2">ID: {userId}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Utilisateur non trouvé</h2>
          <Link
            to="/administration/users"
            className="text-orange-600 hover:text-orange-700"
          >
            Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profil', label: 'Profil', icon: <UserIcon className="h-4 w-4" /> },
    { id: 'connexions', label: 'Historique connexions', icon: <ClockIcon className="h-4 w-4" /> },
    { id: 'actions', label: 'Historique actions', icon: <DocumentTextIcon className="h-4 w-4" /> }
  ];

  // Données formatées
  const formattedSites = getFormattedSites();
 
  
  const formattedConnections = getFormattedConnections();
  const formattedActions = getFormattedActions();

  const formatRoleLabel = (roleName: string): string => {
    const labels: Record<string, string> = {
      PLATFORM_SUPER_ADMIN: 'Super administrateur',
      PLATFORM_ADMIN: 'Administrateur plateforme',
      ADMINISTRATEUR: 'Administrateur',
      SUPERVISEUR: 'Superviseur',
      OPERATEUR: 'Opérateur',
      OPERATOR: 'Opérateur',
      AGENT_QUALITE: 'Agent qualité',
      QUALITY_AGENT: 'Agent qualité',
      STOCK: 'Gestionnaire stock',
      COMMERCIAL: 'Commercial',
      FINANCE: 'Finance',
      AUDITEUR: 'Auditeur',
      TENANT_OWNER: 'Propriétaire tenant',
      TENANT_ADMIN: 'Admin tenant',
      SITE_ADMIN: 'Admin site',
      SITE_MANAGER: 'Manager site',
    };
    return labels[roleName] || roleName.replace(/_/g, ' ');
  };

  const getPlatformRoles = (): string[] => {
    const roles: string[] = [];
    if (user?.is_superuser) roles.push('Super administrateur');
    else if (user?.is_staff) roles.push('Administrateur plateforme');
    globalRoles.forEach((role) => roles.push(formatRoleLabel(role)));
    if (user?.role?.name) roles.push(formatRoleLabel(user.role.name));
    return [...new Set(roles)];
  };

  const getSiteRoleLabels = (): string[] => {
    return [...new Set(
      userAssignments
        .filter((assignment) => assignment.is_active)
        .map((assignment) => assignment.role_label || assignment.role_key || '')
        .filter(Boolean)
    )];
  };

  const platformRoles = getPlatformRoles();
  const siteRoleLabels = getSiteRoleLabels();
  const allRoleLabels = [...new Set([...platformRoles, ...siteRoleLabels])];

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link
                to="/administration/users"
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <UserIcon className="w-8 h-8 text-orange-500 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="font-medium">@{user.username}</span>
                  {user.email && <span>• {user.email}</span>}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                !user.is_blocked 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {!user.is_blocked ? (
                  <>
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    Actif
                  </>
                ) : (
                  <>
                    <XCircleIcon className="h-3 w-3 mr-1" />
                    Bloqué
                  </>
                )}
              </span>
              
              {user.is_blocked ? (
                <button
                  onClick={handleUnblockUser}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow transition-all"
                >
                  <KeyIcon className="h-4 w-4" />
                  Débloquer
                </button>
              ) : (
                <button
                  onClick={() => setShowBlockDialog(true)}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 shadow-sm hover:shadow transition-all"
                >
                  <LockClosedIcon className="h-4 w-4" />
                  Bloquer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation des onglets */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'profil' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Photo de profil */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="text-center">
                    <div className="w-32 h-32 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <UserIcon className="h-16 w-16 text-orange-500" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{user.name}</h3>
                    <p className="text-sm text-gray-500">@{user.username}</p>
                    {allRoleLabels.length > 0 ? (
                      <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                        {allRoleLabels.map((role) => (
                          <span
                            key={role}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-gray-400">Aucun rôle assigné</p>
                    )}
                    <button className="mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors">
                      <PencilIcon className="h-4 w-4" />
                      Modifier la photo
                    </button>
                  </div>
                </div>
              </div>

              {/* Informations du profil */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informations personnelles</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                      <p className="text-sm text-gray-900">{user.name}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur</label>
                      <p className="text-sm text-gray-900">@{user.username}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <p className="text-sm text-gray-900">{user.email || 'Non renseigné'}</p>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <p className="text-sm text-gray-900">{user.phone || 'Non renseigné'}</p>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rôle(s)</label>
                      {allRoleLabels.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {platformRoles.map((role) => (
                            <span
                              key={`global-${role}`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200"
                            >
                              {role}
                              <span className="ml-1 text-indigo-500">(global)</span>
                            </span>
                          ))}
                          {siteRoleLabels
                            .filter((role) => !platformRoles.includes(role))
                            .map((role) => (
                              <span
                                key={`site-${role}`}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                              >
                                {role}
                                <span className="ml-1 text-blue-500">(site)</span>
                              </span>
                            ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Aucun rôle assigné</p>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 mt-6 pt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Informations système</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          !user.is_blocked 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {!user.is_blocked ? 'Actif' : 'Bloqué'}
                        </span>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Compte créé le</label>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">
                            {new Date(user.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dernière connexion</label>
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">
                            {user.last_login
                              ? new Date(user.last_login).toLocaleString('fr-FR')
                              : 'Jamais connecté'}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dernière modification</label>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">
                            {new Date(user.updated_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

                         {/* Carte séparée pour les sites assignés */}
             <div className="mt-6">
               <div className="bg-white rounded-xl shadow-sm p-6">
                 <div className="flex items-center justify-between mb-4">
                   <h3 className="text-lg font-medium text-gray-900">Sites assignés et rôles</h3>
                   <button 
                     onClick={openAssignSiteModal}
                     className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                   >
                     <PlusIcon className="h-4 w-4" />
                     Assigner un site
                   </button>
                 </div>
                 
                 
                 
                 {formattedSites.length > 0 ? (
                   <div className="space-y-3">
                     {formattedSites.map((site) => (
                       <div key={site.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                         <div className="flex items-center">
                           <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                           <div>
                             <div className="text-sm font-medium text-gray-900">{site.name}</div>
                             <div className="text-xs text-gray-500">{site.code}</div>
                           </div>
                         </div>
                         <div className="flex items-center gap-2">
                           <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                             {site.role}
                           </span>
                           {site.is_default && (
                             <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                               Site par défaut
                             </span>
                           )}
                           <span className="text-xs text-gray-500">
                             Assigné le {new Date(site.assigned_at).toLocaleDateString('fr-FR')}
                           </span>
                         </div>
                       </div>
                     ))}
                   </div>
                 ) : (
                   <div className="text-center py-6">
                     <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                     <p className="text-sm text-gray-500">Aucun site assigné</p>
                     <button 
                       onClick={openAssignSiteModal}
                       className="mt-2 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                     >
                       <PlusIcon className="h-4 w-4" />
                       Assigner un site
                     </button>
                   </div>
                 )}
               </div>
             </div>
          </div>
        )}

        {/* Onglet Historique des connexions */}
        {activeTab === 'connexions' && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Historique des connexions</h2>

              {historyLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500">Chargement de l'historique...</p>
                </div>
              ) : historyError ? (
                <div className="text-center py-6">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Historique indisponible</h3>
                  <p className="text-gray-500">{historyError}</p>
                </div>
              ) : formattedConnections.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date et heure
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Adresse IP
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Navigateur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formattedConnections.map((connexion) => (
                        <tr key={connexion.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {new Date(connexion.date).toLocaleString('fr-FR')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {connexion.ip}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {connexion.user_agent}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              connexion.status === 'success'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-red-100 text-red-800 border border-red-200'
                            }`}>
                              {connexion.status === 'success'
                                ? (connexion.twoFactor ? 'Succès (2FA)' : 'Succès')
                                : 'Échec'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune connexion enregistrée</h3>
                  <p className="text-gray-500">Aucune session de connexion n'a été trouvée pour cet utilisateur.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet Historique des actions */}
        {activeTab === 'actions' && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Historique des actions</h2>

              {historyLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500">Chargement de l'historique...</p>
                </div>
              ) : historyError ? (
                <div className="text-center py-6">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Historique indisponible</h3>
                  <p className="text-gray-500">{historyError}</p>
                </div>
              ) : formattedActions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date et heure
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Effectuée par
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Détails
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Adresse IP
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formattedActions.map((action) => (
                        <tr key={action.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {new Date(action.date).toLocaleString('fr-FR')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">{action.action}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {action.actor}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate" title={action.details}>
                            {action.details}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {action.ip}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune action enregistrée</h3>
                  <p className="text-gray-500">Aucun log d'audit n'a été trouvé pour cet utilisateur.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de blocage */}
      <Dialog
        isOpen={showBlockDialog}
        onClose={() => setShowBlockDialog(false)}
        title="Bloquer l'utilisateur"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir bloquer l'utilisateur <strong>{user?.name}</strong> ?
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Raison du blocage *
            </label>
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Expliquez la raison du blocage..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              rows={3}
              required
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowBlockDialog(false)}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleBlockUser}
              disabled={!blockReason.trim()}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Bloquer l'utilisateur
            </button>
          </div>
        </div>
      </Dialog>

      {/* Modal d'assignation de site */}
      <AssignSiteModal
        isOpen={showAssignSiteModal}
        onClose={() => setShowAssignSiteModal(false)}
        onAssign={handleAssignSite}
        sites={availableSites}
        roles={availableRoles.map(role => ({
          ...role,
          id: String(role.id)
        }))}
        userId={Number(user.id)}
        userName={user.username}
        userEmail={user.email}
        userFirstName={user.name ? user.name.split(' ')[0] : ''}
        userLastName={user.name ? user.name.split(' ').slice(1).join(' ') : ''}
        loading={assigningSite}
      />

      {/* Container des toasts */}
      <ToastContainer toasts={toasts} position="top-right" />
    </div>
  );
};

export default UserDetail;
